/**
 * adminProductsSlice — Redux Toolkit slice for admin product management.
 *
 * State shape
 * -----------
 * - items: AdminProductDTO[]        — current page of products (list view)
 * - total: number                   — total count from the API
 * - currentPage: number             — current page
 * - perPage: number                 — page size
 * - loading: boolean                — list fetch in progress
 * - error: AdminProductsError | null — last error (preserves full platform envelope)
 * - currentProduct: AdminProductDTO | null — product open in the editor
 * - mutating: boolean               — write operation in progress
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only page-level containers do.
 *
 * CON-PRODUCT-001.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createAdminProduct,
  deleteAdminProduct,
  fetchAdminProduct,
  fetchAdminProducts,
  getAdminProductOptions,
  publishAdminProduct,
  unpublishAdminProduct,
  updateAdminProduct,
} from '@dfq/shared/api/admin/products';
import { fetchAdminProductTypeConfigs } from '@dfq/shared/api/admin/productTypeConfigs';
import { fetchAdminCategoryTree } from '@dfq/shared/api/admin/categories';
import { isApiError } from '@dfq/shared/api/_envelope';
import { flattenCategoryTree, type FlatCategoryOption } from '@dfq/shared/lib/flattenCategoryTree';
import type {
  AdminProductCreatePayload,
  AdminProductDTO,
  AdminProductFormOptionsDTO,
  AdminProductsListParams,
  AdminProductUpdatePayload,
} from '@dfq/shared/types/product';
import type { ProductTypeConfigListItemDTO } from '@dfq/shared/types/productTypes';

// ---------------------------------------------------------------------------
// Error shape — preserves the full platform error envelope
// ---------------------------------------------------------------------------

export interface AdminProductsError {
  message: string;
  severity?: string;
  errors?: Record<string, string[]>;
  errorCode?: string;
  status: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface AdminProductsState {
  items: AdminProductDTO[];
  total: number;
  currentPage: number;
  perPage: number;
  loading: boolean;
  error: AdminProductsError | null;
  currentProduct: AdminProductDTO | null;
  mutating: boolean;
  /** Enum value sets for the create/edit form (productType, sizingMode, pricingUnit). */
  formOptions: AdminProductFormOptionsDTO | null;
  /** Active product-type configs the form's configurator dropdown lists. */
  configuratorOptions: ProductTypeConfigListItemDTO[];
  /** Flattened, depth-annotated category tree the form's category picker lists. */
  categoryOptions: FlatCategoryOption[];
  formOptionsLoading: boolean;
}

const initialState: AdminProductsState = {
  items: [],
  total: 0,
  currentPage: 1,
  perPage: 25,
  loading: false,
  error: null,
  currentProduct: null,
  mutating: false,
  formOptions: null,
  configuratorOptions: [],
  categoryOptions: [],
  formOptionsLoading: false,
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchAdminProductsThunk = createAsyncThunk(
  'adminProducts/fetchList',
  async (params: AdminProductsListParams = {}, { rejectWithValue }) => {
    try {
      return await fetchAdminProducts(params);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch products';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const fetchAdminProductThunk = createAsyncThunk(
  'adminProducts/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await fetchAdminProduct(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const createAdminProductThunk = createAsyncThunk(
  'adminProducts/create',
  async (payload: AdminProductCreatePayload, { rejectWithValue }) => {
    try {
      return await createAdminProduct(payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to create product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const updateAdminProductThunk = createAsyncThunk(
  'adminProducts/update',
  async (
    { id, ...payload }: { id: number } & AdminProductUpdatePayload,
    { rejectWithValue },
  ) => {
    try {
      return await updateAdminProduct(id, payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to update product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const deleteAdminProductThunk = createAsyncThunk(
  'adminProducts/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAdminProduct(id);
      return id;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to delete product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const publishAdminProductThunk = createAsyncThunk(
  'adminProducts/publish',
  async (id: number, { rejectWithValue }) => {
    try {
      return await publishAdminProduct(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to publish product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

export const unpublishAdminProductThunk = createAsyncThunk(
  'adminProducts/unpublish',
  async (id: number, { rejectWithValue }) => {
    try {
      return await unpublishAdminProduct(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to unpublish product';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

/**
 * Fetch the product form's enum options and eligible configurators in one
 * call. Both are needed to render the configurator/product-type/sizing-mode/
 * pricing-unit fields; loading them together avoids two separate loading
 * flags in every consuming view. CON-PROD-101.
 */
export const fetchAdminProductFormOptionsThunk = createAsyncThunk(
  'adminProducts/fetchFormOptions',
  async (_: void, { rejectWithValue }) => {
    try {
      const [options, { productTypeConfigs }, categoryTree] = await Promise.all([
        getAdminProductOptions(),
        fetchAdminProductTypeConfigs({ perPage: 100, status: 'active' }),
        fetchAdminCategoryTree(),
      ]);
      return {
        options,
        configuratorOptions: productTypeConfigs,
        categoryOptions: flattenCategoryTree(categoryTree),
      };
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          message: err.message,
          severity: err.severity,
          errors: err.errors,
          errorCode: err.errorCode,
          status: err.status,
        } satisfies AdminProductsError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch product form options';
      return rejectWithValue({ message: msg, status: 0 } satisfies AdminProductsError);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const adminProductsSlice = createSlice({
  name: 'adminProducts',
  initialState,
  reducers: {
    clearCurrentProduct(state) {
      state.currentProduct = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchAdminProductsThunk (list)
    builder
      .addCase(fetchAdminProductsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProductsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.products;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
        state.perPage = action.payload.perPage;
      })
      .addCase(fetchAdminProductsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch products', status: 0 };
      });

    // fetchAdminProductThunk (single)
    builder
      .addCase(fetchAdminProductThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProductThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchAdminProductThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch product', status: 0 };
      });

    // createAdminProductThunk
    builder
      .addCase(createAdminProductThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(createAdminProductThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentProduct = action.payload;
      })
      .addCase(createAdminProductThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to create product', status: 0 };
      });

    // updateAdminProductThunk
    builder
      .addCase(updateAdminProductThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(updateAdminProductThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentProduct = action.payload;
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(updateAdminProductThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to update product', status: 0 };
      });

    // deleteAdminProductThunk
    builder
      .addCase(deleteAdminProductThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(deleteAdminProductThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.items = state.items.filter((p) => p.id !== action.payload);
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
      })
      .addCase(deleteAdminProductThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to delete product', status: 0 };
      });

    // publishAdminProductThunk
    builder
      .addCase(publishAdminProductThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(publishAdminProductThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentProduct = action.payload;
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(publishAdminProductThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to publish product', status: 0 };
      });

    // unpublishAdminProductThunk
    builder
      .addCase(unpublishAdminProductThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(unpublishAdminProductThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentProduct = action.payload;
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(unpublishAdminProductThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to unpublish product', status: 0 };
      });

    // fetchAdminProductFormOptionsThunk
    builder
      .addCase(fetchAdminProductFormOptionsThunk.pending, (state) => {
        state.formOptionsLoading = true;
      })
      .addCase(fetchAdminProductFormOptionsThunk.fulfilled, (state, action) => {
        state.formOptionsLoading = false;
        state.formOptions = action.payload.options;
        state.configuratorOptions = action.payload.configuratorOptions;
        state.categoryOptions = action.payload.categoryOptions;
      })
      .addCase(fetchAdminProductFormOptionsThunk.rejected, (state, action) => {
        state.formOptionsLoading = false;
        state.error =
          (action.payload as AdminProductsError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch product form options', status: 0 };
      });
  },
});

export const { clearCurrentProduct, clearError: clearAdminProductError } =
  adminProductsSlice.actions;

export const adminProductsReducer = adminProductsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface AdminProductsRootState {
  adminProducts: AdminProductsState;
}

export const selectAdminProducts = (
  state: AdminProductsRootState,
): AdminProductDTO[] => state.adminProducts.items;

export const selectAdminProductsLoading = (
  state: AdminProductsRootState,
): boolean => state.adminProducts.loading;

export const selectAdminProductsError = (
  state: AdminProductsRootState,
): AdminProductsError | null => state.adminProducts.error;

export const selectCurrentAdminProduct = (
  state: AdminProductsRootState,
): AdminProductDTO | null => state.adminProducts.currentProduct;

export const selectAdminProductsMutating = (
  state: AdminProductsRootState,
): boolean => state.adminProducts.mutating;

export const selectAdminProductsTotal = (
  state: AdminProductsRootState,
): number => state.adminProducts.total;

export const selectAdminProductsPage = (
  state: AdminProductsRootState,
): number => state.adminProducts.currentPage;

export const selectAdminProductsPerPage = (
  state: AdminProductsRootState,
): number => state.adminProducts.perPage;

export const selectAdminProductFormOptions = (
  state: AdminProductsRootState,
): AdminProductFormOptionsDTO | null => state.adminProducts.formOptions;

export const selectAdminProductConfiguratorOptions = (
  state: AdminProductsRootState,
): ProductTypeConfigListItemDTO[] => state.adminProducts.configuratorOptions;

export const selectAdminProductFormOptionsLoading = (
  state: AdminProductsRootState,
): boolean => state.adminProducts.formOptionsLoading;

export const selectAdminProductCategoryOptions = (
  state: AdminProductsRootState,
): FlatCategoryOption[] => state.adminProducts.categoryOptions;
